import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { MessageService } from '../../project-intake/services/message.service';
import { files } from 'jszip';
import { ChangeDetectorRef } from '@angular/core';
import { UnsavedChangesService } from '../../unsaved-changes.service';

/**
 * Usage:
 * 
 * <app-flexible-file-upload
    [acceptedFileTypes]="'.xlsx,.xls,.csv,.txt,.pdf'"
    [showAdditionalInfo]="true"
    [allowMultipleFiles]="true"
    (fileUploaded)="handleFileUpload($event)">
    </app-flexible-file-upload>
 */
export interface FileUploadModel {
  fileName?: string;
  base64Bytes?: string | null;
  rowsTotalCount?: number;
  fileChanged?: boolean | false;
  inputFIleID?: string | '';
  isFileDelted?: boolean | false;
}

@Component({
  selector: 'app-flexible-file-upload',
  templateUrl: './flexible-file-upload.component.html',
  styleUrl: './flexible-file-upload.component.scss'
})
export class FlexibleFileUploadComponent {
  @Input() acceptedFileTypes: string = '';
  @Input() downloadLinkName: string = 'FileName';
  @Input() showAdditionalInfo: boolean = true;
  @Input() allowMultipleFiles: boolean = false;
  @Input() selectedFiles: FileUploadModel | null = null;
  @Input() disableControl: boolean | false = false;
  @Input() inputFIleID: string | null | undefined = 'genericFile';
  @Output() fileUploaded = new EventEmitter<any>();
  @Output() multipleFileDelete = new EventEmitter<{ files: FileUploadModel[], rowIndex?: number, isFileDeleted?: boolean }>();
  @Input() disableAfterReview: boolean = false;

  @Input() selFiles: FileUploadModel[] | null = null;
  @ViewChild('fileInput') fileInputRef: ElementRef | undefined;



  maxSize = 5 * 1024 * 1024; // 5 MB
  totalCount: number = 0;
  isFileUploaded: boolean = false;
  contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

constructor(
    private ngxService: NgxUiLoaderService,
    private msgService: MessageService,
    private unsavedChangesService: UnsavedChangesService,
    private cdr: ChangeDetectorRef) {
      
  }
  ngOnInit() {
    this.resetForm();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedFiles'] && this.selectedFiles! != null || changes['selFiles'] && this.selFiles! != null) {
      if (this.selFiles && this.selFiles.length > 0) {        
        this.showAdditionalInfo = true;
      } else {
        this.showAdditionalInfo = false;
      }


      if ( this.selectedFiles && this.selectedFiles!.inputFIleID == this.inputFIleID) {
        if(this.allowMultipleFiles == false)
        {
          this.showAdditionalInfo = true;
          this.selFiles = [];
          this.selFiles?.push(this.selectedFiles!);
        }
      }
      this.cdr.detectChanges();
    }
  }


  async convertToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = error => reject(error);
    });
  }

  async getExcelRowCount(file: File): Promise<number> {
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const worksheet = workbook.worksheets[0];
    return worksheet.rowCount;
  }

  // removeFile(fileControlId: string): void {
  //   // this.selectedFile = null;
  //   // this.fileName = '';
  //   // this.totalCount = "";
  //   // this.displayTotalCount = "";
  //   // this.isFileUploaded = false;
  //   if (!this.disableControl) {
  //     this.isFileUploaded = false;
  //     this.showAdditionalInfo = false;
  //     this.selectedFiles = {
  //       fileName: '',
  //       base64Bytes: '',
  //       rowsTotalCount: 0,
  //       inputFIleID: this.inputFIleID!,
  //       isFileDelted: true
  //     };

  //     if (this.fileInputRef) {
  //       this.fileInputRef.nativeElement.value = '';
  //     }

  //     if (fileControlId == this.inputFIleID) {
  //       this.fileUploaded.emit(this.selectedFiles!);
  //     }
  //   }
  // }

  async onFileDownload(event: Event, rowIndex: number) {
    this.ngxService.start();
    event.preventDefault(); // Prevent the default behavior (navigation)
    event.stopPropagation(); // Stop the event from bubbling up

    let blob = this.base64ToBlob(this.selFiles![rowIndex]?.base64Bytes!, this.contentType);

    if (this.selFiles![rowIndex]?.fileName! != null
      && this.selFiles![rowIndex]?.fileName! != undefined
      && this.selFiles![rowIndex]?.fileName!.length > 0
    ) {
      let fName = this.selFiles![rowIndex]?.fileName!.split('.');

      this.downloadFile(blob, fName[0], fName[1]);
    }

    //this.downloadFile(blob);
    this.ngxService.stop();
  }

  base64ToBlob(base64: string, contentType: string): Blob {
    const byteCharacters = atob(base64);
    const byteLength = byteCharacters.length;
    const byteArray = new Uint8Array(byteLength);

    for (let i = 0; i < byteLength; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }

    return new Blob([byteArray], { type: contentType });
  }

  downloadFile(blobContent: Blob, fileName: string, fileExtension: string): void {
    let mimeType: string;

    switch (fileExtension) {
      case 'pdf':
        mimeType = 'application/pdf';
        break;
      case 'eml':
        mimeType = 'message/rfc822';
        break;
      case 'xlsx':
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        break;
      case 'csv':
        mimeType = 'text/csv';
        break;
      default:
        mimeType = 'application/octet-stream';
    }

    const blob = new Blob([blobContent], { type: mimeType });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.${fileExtension}`;
    document.body.appendChild(a);
    a.click();

    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  resetForm()
  {
    this.isFileUploaded = false;
    this.showAdditionalInfo = false;
    this.selFiles = [];
  }

  async onFileSelected(event: Event, fileControlId: string): Promise<void> {
    const elem = event.target as HTMLInputElement;
    if (elem.files && elem.files.length > 0) {
      const file = elem.files[0];
      const fileExtension = "." + file.name.split('.').pop()?.toLowerCase();
      //alert(this.acceptedFileTypes + "  $$$ " + fileExtension);
      if(file.size > this.maxSize)
      {
        this.msgService.htmlError('File size cannot exceed 5 MB');
        this.selectedFiles = {
          fileName: '',
          base64Bytes: '',
          rowsTotalCount: 0,
          fileChanged: false,
          inputFIleID: this.inputFIleID!,
          isFileDelted: false
        };
      }
      else if(this.acceptedFileTypes != "*/*" && this.acceptedFileTypes != fileExtension)
      {
        this.msgService.htmlError('File type not allowed');
        this.selectedFiles = {
          fileName: '',
          base64Bytes: '',
          rowsTotalCount: 0,
          fileChanged: false,
          inputFIleID: this.inputFIleID!,
          isFileDelted: false
        };
      }
      else
      {
        this.isFileUploaded = true;
        this.unsavedChangesService.setUnsavedChanges(true);
        this.showAdditionalInfo = true;
  
        if(this.allowMultipleFiles == false)
        {
          this.selFiles = [];
        }

        // Loop through the selected files
        for (let i = 0; i < elem.files.length; i++) {
          const file = elem.files[i];

          this.selFiles!.push(
            this.selectedFiles = {
              fileName: file.name,
              base64Bytes: await this.convertToBase64(file),
              rowsTotalCount: this.totalCount,
              fileChanged: true,
              inputFIleID: this.inputFIleID!,
              isFileDelted: false
            }
          );
        }
        
      }      
    }
    else {
      this.resetForm();
    }

    if (fileControlId == this.inputFIleID) {
      this.emitDataHandle();
    }

    if (this.fileInputRef) {
      this.fileInputRef.nativeElement.value = '';
    }
  }

  removeFile(fileControlId: string, rowIndex: number): void {
    // this.selectedFile = null;
    // this.fileName = '';
    // this.totalCount = "";
    // this.displayTotalCount = "";
    // this.isFileUploaded = false;
    if (!this.disableControl) {      

      if (fileControlId == this.inputFIleID) {

        let clearAll: boolean = false;

        if(this.allowMultipleFiles)
        {
          if(this.selFiles!.length == 1)
          {
            clearAll = true;
          }
          else
          {
            this.selFiles!.splice(rowIndex, 1); 
          }
        }
        else
        {
          clearAll = true;
        }

        if(clearAll)
        {
          this.resetForm();
  
          if (this.fileInputRef) {
            this.fileInputRef.nativeElement.value = '';
          }

          this.selFiles = []
          this.selFiles.push(
            {
              fileName: '',
              base64Bytes: '',
              rowsTotalCount: 0,
              inputFIleID: this.inputFIleID!,
              isFileDelted: true
            }
          );
        }

        this.emitDataHandle(rowIndex);
      }
    }
  }

  emitDataHandle(rowIndex?:any)
  {
    if(this.allowMultipleFiles)
    {
      this.fileUploaded.emit(this.selFiles!)
      this.multipleFileDelete.emit({ files: this.selFiles!, rowIndex ,isFileDeleted:true});
    }
    else
    {
      if(this.selFiles! !=null
        && this.selFiles! != undefined
        && this.selFiles!.length == 1
      )
      {
        this.fileUploaded.emit(this.selFiles![0]);
      }
    }
  }
}


























  // async onFileSelected(event: Event, fileControlId: string): Promise<void> {
  //   debugger;
  //   const elem = event.target as HTMLInputElement;
  //   if (elem.files && elem.files.length > 0) {
  //     const file = elem.files[0];
  //     const fileExtension = "." + file.name.split('.').pop()?.toLowerCase();
  //     //alert(this.acceptedFileTypes + "  $$$ " + fileExtension);
  //     if(file.size > this.maxSize)
  //     {
  //       this.msgService.htmlError('File size cannot exceed 5 MB');
  //       this.selectedFiles = {
  //         fileName: '',
  //         base64Bytes: '',
  //         rowsTotalCount: 0,
  //         fileChanged: false,
  //         inputFIleID: this.inputFIleID!,
  //         isFileDelted: false
  //       };
  //     }
  //     else if(this.acceptedFileTypes != "*/*" && this.acceptedFileTypes != fileExtension)
  //     {
  //       this.msgService.htmlError('File type not allowed');
  //       this.selectedFiles = {
  //         fileName: '',
  //         base64Bytes: '',
  //         rowsTotalCount: 0,
  //         fileChanged: false,
  //         inputFIleID: this.inputFIleID!,
  //         isFileDelted: false
  //       };
  //     }
  //     else
  //     {
  //       this.isFileUploaded = true;
  //       this.showAdditionalInfo = true;
  
  //       // if (this.showAdditionalInfo && file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
  //       //   this.totalCount = await this.getExcelRowCount(file);          
  //       // }
  
  //       this.selectedFiles = {
  //         fileName: file.name,
  //         base64Bytes: await this.convertToBase64(file),
  //         rowsTotalCount: this.totalCount,
  //         fileChanged: true,
  //         inputFIleID: this.inputFIleID!,
  //         isFileDelted: false
  //       }; 
  //     }      
  //   }
  //   else {
  //     this.selectedFiles = {
  //       fileName: '',
  //       base64Bytes: '',
  //       rowsTotalCount: 0,
  //       fileChanged: false,
  //       inputFIleID: this.inputFIleID!,
  //       isFileDelted: false
  //     };
  //   }

  //   if (fileControlId == this.inputFIleID) {
  //     this.fileUploaded.emit(this.selectedFiles!);
  //   }
  //   if (this.fileInputRef) {
  //     this.fileInputRef.nativeElement.value = '';
  //   }
  // }

  
  // resetForm()
  // {
  //   this.isFileUploaded = false;
  //   this.showAdditionalInfo = false;
  //   this.selectedFiles = {
  //     fileName: '',
  //     base64Bytes: '',
  //     rowsTotalCount: 0,
  //     fileChanged: false,
  //     isFileDelted: false
  //   };
  // }
